import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  AI_RISK_REPORT_PDF_KEEP_WITH_NEXT_ATTRIBUTES,
  AI_RISK_REPORT_PDF_PAGE_BREAK_PROTECTED_ATTRIBUTES,
} from '../../data/constants'
import { useAIRiskReportExportMode } from '../RenderMode/AIRiskReportRenderModeProvider'
import styles from './ReportTable.module.scss'

export default function ReportTable({ columns, rows, empty = null, footer = null }) {
  const [sort, setSort] = useState(null)
  const isExportMode = useAIRiskReportExportMode()

  const sortedRows = useMemo(() => {
    if (isExportMode || !sort) return rows
    const column = columns.find((entry) => entry.key === sort.key)
    if (!column) return rows
    const getSortValue = column.sortValue || ((row) => row[column.key])
    const direction = sort.direction === 'desc' ? -1 : 1
    return [...rows].sort((first, second) => {
      const firstValue = getSortValue(first)
      const secondValue = getSortValue(second)
      if (firstValue == null && secondValue == null) return 0
      if (firstValue == null) return 1 * direction
      if (secondValue == null) return -1 * direction
      if (typeof firstValue === 'number' && typeof secondValue === 'number')
        return (firstValue - secondValue) * direction
      return String(firstValue).localeCompare(String(secondValue)) * direction
    })
  }, [columns, isExportMode, rows, sort])

  const toggleSort = (key) => {
    setSort((previousSort) => {
      if (!previousSort || previousSort.key !== key) return { key, direction: 'asc' }
      if (previousSort.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getSortArrow = (column) => {
    if (sort?.key !== column.key) return '⇅'
    return sort.direction === 'asc' ? '▲' : '▼'
  }

  const getAriaSort = (column) => {
    if (isExportMode) return undefined
    if (sort?.key !== column.key) return undefined
    return sort.direction === 'asc' ? 'ascending' : 'descending'
  }

  const getColumnClassName = (column) =>
    [column.className, column.sortable && !isExportMode ? styles.headerSortable : ''].filter(Boolean).join(' ') ||
    undefined

  if (rows.length === 0) return empty

  const wrapClassName = [styles.wrap, isExportMode ? styles.exportWrap : ''].filter(Boolean).join(' ')

  return (
    <div className={wrapClassName}>
      <table className={`${styles.table} w-100`}>
        <thead {...AI_RISK_REPORT_PDF_KEEP_WITH_NEXT_ATTRIBUTES}>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={getColumnClassName(column)} aria-sort={getAriaSort(column)}>
                {column.sortable && !isExportMode ? (
                  <button type="button" className={styles.sortButton} onClick={() => toggleSort(column.key)}>
                    {column.header}
                    <span
                      className={`${styles.sortIndicator} ${sort?.key === column.key ? styles.sortIndicatorActive : ''}`}
                      aria-hidden="true"
                    >
                      {getSortArrow(column)}
                    </span>
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.key}>
              {columns.map((column) => (
                <td key={column.key} className={column.className}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {footer && (
        <div className={styles.footer} {...AI_RISK_REPORT_PDF_PAGE_BREAK_PROTECTED_ATTRIBUTES}>
          {footer}
        </div>
      )}
    </div>
  )
}

ReportTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node.isRequired,
      render: PropTypes.func.isRequired,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
      className: PropTypes.string,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired,
  empty: PropTypes.node,
  footer: PropTypes.node,
}
